import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, CarIcon, DumbbellIcon, BriefcaseIcon, StarIcon, BookOpenIcon, BrainIcon, WrenchIcon } from 'lucide-react';
interface Player {
  id: string;
  name: string;
  color: string;
  pathIndex: number | null;
  avatar?: string | null;
}
interface JourneyStartSceneProps {
  players: Player[];
  onStartPlaying: () => void;
}
const PATH_COLORS = [{
  name: 'School',
  glow: '#a855f7',
  lane: '#7c3aed',
  tile: '#e9d5ff',
  text: '#6b21a8',
  icon: GraduationCapIcon
}, {
  name: 'Workforce',
  glow: '#ec4899',
  lane: '#db2777',
  tile: '#fbcfe8',
  text: '#9d174d',
  icon: BriefcaseIcon
}, {
  name: 'Trade',
  glow: '#f97316',
  lane: '#ea580c',
  tile: '#fed7aa',
  text: '#9a3412',
  icon: WrenchIcon
}, {
  name: 'Self-Made',
  glow: '#14b8a6',
  lane: '#0d9488',
  tile: '#ccfbf1',
  text: '#115e59',
  icon: StarIcon
}];
// Tiles laid out by lane (column) and row (0 = nearest, higher = farther)
type Tile = {
  lane: number;
  row: number;
  icon: ComponentType<any>;
  text: string;
  amount: string;
  positive: boolean;
};
const TILES: Tile[] = [
// Row 0 — nearest (largest)
{
  lane: 0,
  row: 0,
  icon: BookOpenIcon,
  text: 'Study Hard\nPay Off',
  amount: '+$150',
  positive: true
}, {
  lane: 1,
  row: 0,
  icon: BriefcaseIcon,
  text: 'Got a\nPart-Time Job',
  amount: '+$120',
  positive: true
}, {
  lane: 2,
  row: 0,
  icon: CarIcon,
  text: 'Car Broke\nDown',
  amount: '-$200',
  positive: false
}, {
  lane: 3,
  row: 0,
  icon: StarIcon,
  text: 'Side Hustle\nSuccess',
  amount: '+$300',
  positive: true
},
// Row 1 — mid
{
  lane: 0,
  row: 1,
  icon: GraduationCapIcon,
  text: 'Kid Needed\nSchool Supplies',
  amount: '-$80',
  positive: false
}, {
  lane: 1,
  row: 1,
  icon: BriefcaseIcon,
  text: 'No Income\nThis Week',
  amount: '-$100',
  positive: false
}, {
  lane: 2,
  row: 1,
  icon: CarIcon,
  text: 'Car Broke\nDown',
  amount: '-$200',
  positive: false
}, {
  lane: 3,
  row: 1,
  icon: BrainIcon,
  text: 'Invest in\nYourself',
  amount: '+$250',
  positive: true
},
// Row 2 — far (small labels)
{
  lane: 0,
  row: 2,
  icon: BookOpenIcon,
  text: 'Pop Quiz',
  amount: '+$40',
  positive: true
}, {
  lane: 1,
  row: 2,
  icon: BriefcaseIcon,
  text: 'Promotion',
  amount: '+$200',
  positive: true
}, {
  lane: 2,
  row: 2,
  icon: WrenchIcon,
  text: 'New Tools',
  amount: '-$120',
  positive: false
}, {
  lane: 3,
  row: 2,
  icon: StarIcon,
  text: 'Compliment',
  amount: '+$0',
  positive: true
}];
export function JourneyStartScene({
  players,
  onStartPlaying
}: JourneyStartSceneProps) {
  const playersByPath: Record<number, Player[]> = {
    0: [],
    1: [],
    2: [],
    3: []
  };
  players.forEach(p => {
    const idx = p.pathIndex !== null && p.pathIndex !== undefined ? Math.max(0, Math.min(3, p.pathIndex)) : 0;
    playersByPath[idx].push(p);
  });
  // Lane geometry — 4 trapezoids converging to vanishing point at top-center.
  // Bottom of lane: wide (x from leftBottom to rightBottom)
  // Top of lane: narrow (converges at vanishingX = 50%)
  const VANISH_X = 50; // %
  const VANISH_Y = 38; // % — sunset point
  const ROAD_BOTTOM_LEFT = 8; // %
  const ROAD_BOTTOM_RIGHT = 92; // %
  const ROAD_BOTTOM_Y = 100; // %
  const LANE_COUNT = 4;
  // Compute lane bottom edges (in %)
  const laneBottoms = Array.from({
    length: LANE_COUNT + 1
  }, (_, i) => {
    return ROAD_BOTTOM_LEFT + i / LANE_COUNT * (ROAD_BOTTOM_RIGHT - ROAD_BOTTOM_LEFT);
  });
  // At vanishing point, all lanes converge to a small spread
  const vanishSpread = 1.5; // %
  const laneTops = Array.from({
    length: LANE_COUNT + 1
  }, (_, i) => {
    return VANISH_X + (i - LANE_COUNT / 2) * (vanishSpread * 2 / LANE_COUNT);
  });
  // Position helpers — interpolate between bottom (t=0) and vanishing point (t=1)
  const interp = (laneIndex: number, t: number) => {
    // t = 0 at road bottom, 1 at vanishing point
    const bottomCenter = (laneBottoms[laneIndex] + laneBottoms[laneIndex + 1]) / 2;
    const x = bottomCenter + (VANISH_X - bottomCenter) * t;
    const y = ROAD_BOTTOM_Y + (VANISH_Y - ROAD_BOTTOM_Y) * t;
    const scale = 1 - t * 0.85; // shrink as we go back
    return {
      x,
      y,
      scale
    };
  };
  // Tile positions: row 0 = t=0.05, row 1 = t=0.32, row 2 = t=0.55
  const rowT = [0.05, 0.3, 0.52];
  return <div className="fixed inset-0 w-full h-full z-30 overflow-hidden bg-black" data-id="element-194">
      {/* SKY + SUNSET BACKGROUND */}
      <div className="absolute inset-0" style={{
      background: 'linear-gradient(180deg, #1a1535 0%, #2d1b4e 25%, #5b2a6b 45%, #c2410c 62%, #fb923c 70%, #1a1535 95%)'
    }} data-id="element-195" />
      {/* Sunset glow at vanishing point */}
      <div className="absolute rounded-full" style={{
      left: `${VANISH_X}%`,
      top: `${VANISH_Y}%`,
      width: '500px',
      height: '500px',
      transform: 'translate(-50%, -50%)',
      background: 'radial-gradient(circle, rgba(255,200,120,0.95) 0%, rgba(251,146,60,0.6) 25%, rgba(190,80,140,0.3) 50%, transparent 70%)',
      filter: 'blur(20px)'
    }} data-id="element-196" />
      {/* Bright sun core */}
      <div className="absolute rounded-full" style={{
      left: `${VANISH_X}%`,
      top: `${VANISH_Y}%`,
      width: '60px',
      height: '60px',
      transform: 'translate(-50%, -50%)',
      background: 'radial-gradient(circle, #fff8dc 0%, #ffd27a 50%, transparent 100%)',
      boxShadow: '0 0 80px 20px rgba(255, 220, 150, 0.8)'
    }} data-id="element-197" />

      {/* CITY SILHOUETTE — left side */}
      <div className="absolute left-0 top-0 bottom-[30%] w-[35%] pointer-events-none" data-id="element-198">
        <svg viewBox="0 0 400 600" preserveAspectRatio="none" className="w-full h-full" data-id="element-199">
          <defs data-id="element-200">
            <linearGradient id="bldL" x1="0" y1="0" x2="0" y2="1" data-id="element-201">
              <stop offset="0" stopColor="#1a0f2e" data-id="element-202" />
              <stop offset="1" stopColor="#0a0518" data-id="element-203" />
            </linearGradient>
          </defs>
          <polygon points="0,600 0,80 60,80 60,30 120,30 120,100 180,100 180,60 240,60 240,140 300,140 300,90 360,90 360,180 400,180 400,600" fill="url(#bldL)" data-id="element-204" />
          {/* windows */}
          {Array.from({
          length: 60
        }).map((_, i) => <rect key={i} x={10 + i % 8 * 45} y={120 + Math.floor(i / 8) * 50} width="6" height="10" fill={Math.random() > 0.5 ? '#fde047' : '#fb923c'} opacity={0.6 + Math.random() * 0.4} data-id="element-205" />)}
        </svg>
      </div>

      {/* CITY SILHOUETTE — right side */}
      <div className="absolute right-0 top-0 bottom-[30%] w-[35%] pointer-events-none" data-id="element-206">
        <svg viewBox="0 0 400 600" preserveAspectRatio="none" className="w-full h-full" data-id="element-207">
          <defs data-id="element-208">
            <linearGradient id="bldR" x1="0" y1="0" x2="0" y2="1" data-id="element-209">
              <stop offset="0" stopColor="#1a0f2e" data-id="element-210" />
              <stop offset="1" stopColor="#0a0518" data-id="element-211" />
            </linearGradient>
          </defs>
          <polygon points="400,600 400,90 340,90 340,40 280,40 280,110 220,110 220,70 160,70 160,150 100,150 100,100 40,100 40,200 0,200 0,600" fill="url(#bldR)" data-id="element-212" />
          {Array.from({
          length: 60
        }).map((_, i) => <rect key={i} x={50 + i % 8 * 45} y={130 + Math.floor(i / 8) * 50} width="6" height="10" fill={Math.random() > 0.5 ? '#fde047' : '#ec4899'} opacity={0.6 + Math.random() * 0.4} data-id="element-213" />)}
        </svg>
      </div>

      {/* TREE silhouettes flanking the road */}
      {[{
      side: 'left',
      x: 2,
      y: 55,
      scale: 1.2
    }, {
      side: 'left',
      x: 6,
      y: 70,
      scale: 1.5
    }, {
      side: 'left',
      x: 12,
      y: 85,
      scale: 1.8
    }, {
      side: 'right',
      x: 98,
      y: 55,
      scale: 1.2
    }, {
      side: 'right',
      x: 94,
      y: 70,
      scale: 1.5
    }, {
      side: 'right',
      x: 88,
      y: 85,
      scale: 1.8
    }].map((t, i) => <div key={i} className="absolute pointer-events-none" style={{
      left: `${t.x}%`,
      top: `${t.y}%`,
      transform: `translate(-50%, -100%) scale(${t.scale})`
    }} data-id="element-214">
          <svg width="80" height="120" viewBox="0 0 80 120" data-id="element-215">
            <ellipse cx="40" cy="40" rx="38" ry="42" fill="#0a0a14" data-id="element-216" />
            <ellipse cx="25" cy="30" rx="20" ry="22" fill="#0f0f1c" data-id="element-217" />
            <ellipse cx="55" cy="35" rx="22" ry="24" fill="#0f0f1c" data-id="element-218" />
            <rect x="36" y="70" width="8" height="50" fill="#050510" data-id="element-219" />
          </svg>
        </div>)}

      {/* TOP TITLE BILLBOARD */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 text-center pointer-events-none" data-id="element-220">
        <motion.h1 className="text-5xl md:text-7xl font-bold leading-none drop-shadow-[0_4px_20px_rgba(236,72,153,0.6)]" style={{
        fontFamily: '"Dancing Script", cursive',
        background: 'linear-gradient(180deg, #fde047 0%, #f97316 30%, #ec4899 60%, #a855f7 100%)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextStroke: '2px rgba(255,255,255,0.15)',
        color: 'transparent',
        textShadow: '0 0 40px rgba(236,72,153,0.5)'
      }} animate={{
        scale: [1, 1.02, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity
      }} data-id="element-221">
          Trippin'
        </motion.h1>
        <h1 className="text-4xl md:text-6xl font-bold leading-none -mt-2" style={{
        fontFamily: '"Dancing Script", cursive',
        background: 'linear-gradient(180deg, #67e8f9 0%, #06b6d4 50%, #0891b2 100%)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        WebkitTextStroke: '2px rgba(255,255,255,0.15)',
        filter: 'drop-shadow(0 0 20px rgba(6,182,212,0.6))'
      }} data-id="element-222">
          Through Life
        </h1>
        <div className="mt-2 inline-block bg-gradient-to-r from-yellow-300 to-orange-400 px-4 py-1 rounded-full border-2 border-yellow-200 shadow-lg" data-id="element-223">
          <p className="text-[10px] md:text-xs font-black tracking-widest text-purple-900" data-id="element-224">
            ROLL THE DICE • MAKE CHOICES • BUILD YOUR LIFE
          </p>
        </div>
      </div>

      {/* THE ROAD — base dark asphalt as a trapezoid */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" style={{
      pointerEvents: 'none'
    }} data-id="element-225">
        <defs data-id="element-226">
          <linearGradient id="asphalt" x1="0" y1="1" x2="0" y2="0" data-id="element-227">
            <stop offset="0" stopColor="#1a1a2e" data-id="element-228" />
            <stop offset="0.6" stopColor="#2d1b4e" data-id="element-229" />
            <stop offset="1" stopColor="#5b2a6b" data-id="element-230" />
          </linearGradient>
        </defs>
        {/* Road trapezoid */}
        <polygon points={`${ROAD_BOTTOM_LEFT},${ROAD_BOTTOM_Y} ${ROAD_BOTTOM_RIGHT},${ROAD_BOTTOM_Y} ${laneTops[LANE_COUNT]},${VANISH_Y} ${laneTops[0]},${VANISH_Y}`} fill="url(#asphalt)" data-id="element-231" />
      </svg>

      {/* GLOWING COLORED LANES (4 trapezoids) */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" style={{
      pointerEvents: 'none'
    }} data-id="element-232">
        {PATH_COLORS.map((p, i) => {
        const points = `${laneBottoms[i]},${ROAD_BOTTOM_Y} ${laneBottoms[i + 1]},${ROAD_BOTTOM_Y} ${laneTops[i + 1]},${VANISH_Y} ${laneTops[i]},${VANISH_Y}`;
        return <g key={i} data-id="element-233">
              {/* Glow underlay */}
              <polygon points={points} fill={p.glow} opacity="0.55" style={{
            filter: `drop-shadow(0 0 20px ${p.glow})`
          }} data-id="element-234" />
              {/* Inner darker fill */}
              <polygon points={points} fill={p.lane} opacity="0.85" data-id="element-235" />
            </g>;
      })}
        {/* Dashed lane dividers */}
        {[1, 2, 3].map(i => {
        const x1 = laneBottoms[i];
        const x2 = laneTops[i];
        // Draw as multiple short dashes interpolating from bottom to top
        const dashes = [];
        for (let k = 0; k < 12; k++) {
          const t1 = k / 12;
          const t2 = t1 + 0.04;
          const yA = ROAD_BOTTOM_Y + (VANISH_Y - ROAD_BOTTOM_Y) * t1;
          const yB = ROAD_BOTTOM_Y + (VANISH_Y - ROAD_BOTTOM_Y) * t2;
          const xA = x1 + (x2 - x1) * t1;
          const xB = x1 + (x2 - x1) * t2;
          dashes.push(<line key={`${i}-${k}`} x1={xA} y1={yA} x2={xB} y2={yB} stroke="white" strokeWidth="0.25" opacity={0.6 - t1 * 0.4} data-id="element-236" />);
        }
        return <g key={`d${i}`} data-id="element-237">{dashes}</g>;
      })}
      </svg>

      {/* TILES on lanes */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-238">
        {TILES.map((tile, idx) => {
        const path = PATH_COLORS[tile.lane];
        const Icon = tile.icon;
        const t = rowT[tile.row];
        const pos = interp(tile.lane, t);
        const baseSize = 130;
        const size = baseSize * pos.scale;
        return <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 + idx * 0.04,
          duration: 0.5
        }} className="absolute rounded-2xl shadow-2xl flex flex-col items-center justify-center text-center" style={{
          left: `${pos.x}%`,
          top: `${pos.y}%`,
          width: `${size}px`,
          height: `${size * 1.15}px`,
          transform: 'translate(-50%, -100%)',
          background: `linear-gradient(180deg, white 0%, ${path.tile} 100%)`,
          border: `${Math.max(1, 3 * pos.scale)}px solid ${path.glow}`,
          boxShadow: `0 ${10 * pos.scale}px ${30 * pos.scale}px rgba(0,0,0,0.4), 0 0 ${20 * pos.scale}px ${path.glow}`,
          padding: `${8 * pos.scale}px`
        }} data-id="element-239">
              <Icon style={{
            width: `${28 * pos.scale}px`,
            height: `${28 * pos.scale}px`,
            color: path.text
          }} data-id="element-240" />
              <p className="font-bold leading-tight whitespace-pre-line mt-1" style={{
            fontSize: `${Math.max(7, 12 * pos.scale)}px`,
            color: path.text
          }} data-id="element-241">
                {tile.text}
              </p>
              <p className="font-black mt-1" style={{
            fontSize: `${Math.max(9, 16 * pos.scale)}px`,
            color: tile.positive ? '#15803d' : '#b91c1c'
          }} data-id="element-242">
                {tile.amount}
              </p>
            </motion.div>;
      })}
      </div>

      {/* PLAYER PAWNS on their chosen lanes (placed near bottom of lane) */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-243">
        {[0, 1, 2, 3].map(laneIdx => {
        const path = PATH_COLORS[laneIdx];
        const pathPlayers = playersByPath[laneIdx];
        if (pathPlayers.length === 0) return null;
        const pos = interp(laneIdx, 0.18);
        return <div key={laneIdx} className="absolute flex flex-col items-center gap-1" style={{
          left: `${pos.x}%`,
          top: `${pos.y}%`,
          transform: 'translate(-50%, -100%)'
        }} data-id="element-244">
              <div className="flex gap-1" data-id="element-245">
                {pathPlayers.map((p, i) => <motion.div key={p.id} animate={{
              y: [0, -6, 0]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              delay: i * 0.15
            }} className="rounded-full border-[3px] border-white overflow-hidden flex items-center justify-center" style={{
              width: 40,
              height: 40,
              background: path.lane,
              boxShadow: `0 0 16px ${path.glow}, 0 4px 8px rgba(0,0,0,0.5)`
            }} data-id="element-246">
                    {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" data-id="element-247" /> : <span className="text-white font-bold text-sm" data-id="element-248">
                        {p.name.charAt(0).toUpperCase()}
                      </span>}
                  </motion.div>)}
              </div>
              <div className="px-2 py-0.5 rounded-full text-[9px] font-bold text-white whitespace-nowrap" style={{
            background: path.glow
          }} data-id="element-249">
                {pathPlayers.map(p => p.name).join(' • ')}
              </div>
            </div>;
      })}
      </div>

      {/* 3D PURPLE PAWN — front center */}
      <motion.div className="absolute z-10 pointer-events-none" style={{
      left: '52%',
      bottom: '8%',
      transform: 'translateX(-50%)'
    }} animate={{
      y: [0, -10, 0]
    }} transition={{
      duration: 2.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-250">
        <svg width="110" height="170" viewBox="0 0 110 170" data-id="element-251">
          <defs data-id="element-252">
            <radialGradient id="pawnHead" cx="0.35" cy="0.3" r="0.8" data-id="element-253">
              <stop offset="0" stopColor="#d8b4fe" data-id="element-254" />
              <stop offset="0.5" stopColor="#9333ea" data-id="element-255" />
              <stop offset="1" stopColor="#4c1d95" data-id="element-256" />
            </radialGradient>
            <radialGradient id="pawnBody" cx="0.35" cy="0.3" r="0.9" data-id="element-257">
              <stop offset="0" stopColor="#c084fc" data-id="element-258" />
              <stop offset="0.6" stopColor="#7e22ce" data-id="element-259" />
              <stop offset="1" stopColor="#3b0764" data-id="element-260" />
            </radialGradient>
            <filter id="pawnSh" x="-30%" y="-10%" width="160%" height="130%" data-id="element-261">
              <feDropShadow dx="0" dy="8" stdDeviation="6" floodOpacity="0.6" floodColor="#7c3aed" data-id="element-262" />
            </filter>
          </defs>
          {/* Base */}
          <ellipse cx="55" cy="160" rx="45" ry="8" fill="rgba(0,0,0,0.4)" data-id="element-263" />
          <ellipse cx="55" cy="155" rx="42" ry="10" fill="url(#pawnBody)" filter="url(#pawnSh)" data-id="element-264" />
          {/* Body */}
          <path d="M 28 155 Q 22 130 32 90 Q 38 70 45 65 L 65 65 Q 72 70 78 90 Q 88 130 82 155 Z" fill="url(#pawnBody)" filter="url(#pawnSh)" data-id="element-265" />
          {/* Neck collar */}
          <ellipse cx="55" cy="65" rx="22" ry="6" fill="url(#pawnBody)" data-id="element-266" />
          {/* Head */}
          <circle cx="55" cy="40" r="26" fill="url(#pawnHead)" filter="url(#pawnSh)" data-id="element-267" />
          {/* Highlight */}
          <ellipse cx="45" cy="30" rx="9" ry="13" fill="white" opacity="0.45" data-id="element-268" />
          <ellipse cx="42" cy="105" rx="6" ry="22" fill="white" opacity="0.18" data-id="element-269" />
        </svg>
      </motion.div>

      {/* 3D DICE — front left */}
      <motion.div className="absolute z-10 pointer-events-none" style={{
      left: '38%',
      bottom: '12%',
      transform: 'translateX(-50%)'
    }} animate={{
      rotate: [0, 5, -5, 0],
      y: [0, -4, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-270">
        <svg width="120" height="130" viewBox="0 0 120 130" data-id="element-271">
          <defs data-id="element-272">
            <linearGradient id="diceTop" x1="0" y1="0" x2="0" y2="1" data-id="element-273">
              <stop offset="0" stopColor="#ffffff" data-id="element-274" />
              <stop offset="1" stopColor="#e5e7eb" data-id="element-275" />
            </linearGradient>
            <linearGradient id="diceLeft" x1="0" y1="0" x2="1" y2="0" data-id="element-276">
              <stop offset="0" stopColor="#9ca3af" data-id="element-277" />
              <stop offset="1" stopColor="#d1d5db" data-id="element-278" />
            </linearGradient>
            <linearGradient id="diceRight" x1="0" y1="0" x2="1" y2="0" data-id="element-279">
              <stop offset="0" stopColor="#e5e7eb" data-id="element-280" />
              <stop offset="1" stopColor="#9ca3af" data-id="element-281" />
            </linearGradient>
          </defs>
          {/* Top face */}
          <polygon points="60,5 110,30 60,55 10,30" fill="url(#diceTop)" stroke="#374151" strokeWidth="1.5" data-id="element-282" />
          {/* Left face */}
          <polygon points="10,30 60,55 60,115 10,90" fill="url(#diceLeft)" stroke="#374151" strokeWidth="1.5" data-id="element-283" />
          {/* Right face */}
          <polygon points="110,30 60,55 60,115 110,90" fill="url(#diceRight)" stroke="#374151" strokeWidth="1.5" data-id="element-284" />
          {/* Top dots (5) */}
          <circle cx="35" cy="22" r="3" fill="#1f2937" data-id="element-285" />
          <circle cx="85" cy="22" r="3" fill="#1f2937" data-id="element-286" />
          <circle cx="60" cy="30" r="3" fill="#1f2937" data-id="element-287" />
          <circle cx="35" cy="38" r="3" fill="#1f2937" data-id="element-288" />
          <circle cx="85" cy="38" r="3" fill="#1f2937" data-id="element-289" />
          {/* Left face dots (3) */}
          <circle cx="22" cy="50" r="3" fill="#1f2937" data-id="element-290" />
          <circle cx="35" cy="72" r="3" fill="#1f2937" data-id="element-291" />
          <circle cx="48" cy="92" r="3" fill="#1f2937" data-id="element-292" />
          {/* Right face dots (4) */}
          <circle cx="75" cy="55" r="3" fill="#1f2937" data-id="element-293" />
          <circle cx="95" cy="50" r="3" fill="#1f2937" data-id="element-294" />
          <circle cx="75" cy="95" r="3" fill="#1f2937" data-id="element-295" />
          <circle cx="95" cy="90" r="3" fill="#1f2937" data-id="element-296" />
        </svg>
      </motion.div>

      {/* CHOOSE YOUR PATH side panel — top left */}
      <div className="absolute left-3 top-[28%] z-20 bg-black/60 backdrop-blur-md rounded-2xl p-3 shadow-2xl border border-white/10" data-id="element-297">
        <p className="text-white font-black text-xs tracking-wider mb-2" data-id="element-298">
          CHOOSE YOUR PATH
        </p>
        <div className="space-y-1.5" data-id="element-299">
          {PATH_COLORS.map(p => {
          const Icon = p.icon;
          return <div key={p.name} className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{
            background: `${p.glow}22`,
            border: `1px solid ${p.glow}66`
          }} data-id="element-300">
                <Icon className="w-4 h-4" style={{
              color: p.glow
            }} data-id="element-301" />
                <span className="text-xs font-bold" style={{
              color: p.glow
            }} data-id="element-302">
                  {p.name}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* TODAY'S CHOICES sign — bottom right */}
      <div className="absolute right-3 bottom-[22%] z-20 bg-black/60 backdrop-blur-md rounded-2xl p-3 shadow-2xl border border-pink-400/40 max-w-[140px]" data-id="element-303">
        <p className="text-pink-300 font-black text-[11px] text-center leading-tight tracking-wider" data-id="element-304">
          TODAY'S CHOICES
        </p>
        <p className="text-yellow-300 font-black text-[11px] text-center leading-tight tracking-wider mt-1" data-id="element-305">
          TOMORROW'S LIFE
        </p>
        <p className="text-center text-lg mt-1" data-id="element-306">✨</p>
      </div>

      {/* Twinkling neon stars */}
      {Array.from({
      length: 30
    }).map((_, i) => <motion.div key={i} className="absolute rounded-full pointer-events-none" style={{
      top: `${Math.random() * 50}%`,
      left: `${Math.random() * 100}%`,
      width: 2 + Math.random() * 2,
      height: 2 + Math.random() * 2,
      background: ['#fde047', '#f9a8d4', '#67e8f9'][i % 3],
      boxShadow: `0 0 8px currentColor`
    }} animate={{
      opacity: [0.2, 1, 0.2]
    }} transition={{
      duration: 1.5 + Math.random() * 2,
      repeat: Infinity,
      delay: Math.random() * 2
    }} data-id="element-307" />)}

      {/* ROLL DICE CTA */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center z-30 px-4" data-id="element-308">
        <motion.button onClick={onStartPlaying} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.96
      }} animate={{
        boxShadow: ['0 0 30px rgba(236,72,153,0.8)', '0 0 60px rgba(236,72,153,1)', '0 0 30px rgba(236,72,153,0.8)']
      }} transition={{
        boxShadow: {
          duration: 1.8,
          repeat: Infinity
        }
      }} className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-pink-600 px-10 py-4 rounded-full border-[3px] border-white shadow-2xl" data-id="element-309">
          <span className="text-white font-black text-lg md:text-xl tracking-wider drop-shadow-lg" data-id="element-310">
            🎲 ROLL DICE
          </span>
          <span className="block text-pink-100 text-[10px] md:text-xs font-bold mt-0.5 tracking-widest" data-id="element-311">
            START YOUR JOURNEY
          </span>
        </motion.button>
      </div>
    </div>;
}